import React, { useEffect, useState, useCallback } from 'react';
import './company.css'
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useAbility } from '../../roles/AbilityContext';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ClipLoader } from "react-spinners";
import { useTranslation } from 'react-i18next';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { formatInTimeZone } from 'date-fns-tz';
import { Company, Connection, Constant, Grid, Status, Vessels } from '../../interfaces/interfaces';
import { deleteCompany, getCompany, getConnection, getConstant, getGridPointsAll, getStatus, getVessel, patchCompany, patchConnection, postCompany, postConnection } from '../../services/requests';
import Table, { ColumnConfig } from '../../components/table/table';

const CompanyComponent: React.FC = () => {
    const [showResolved, setShowResolved] = useState('companyInformation');
    const name = Cookies.get('name');
    const email = Cookies.get('email');
    const phone = Cookies.get('phone');
    const nempresa = Cookies.get('Nempresa');
    const nembarcacion = Cookies.get('Nembarcacion');
    const role = Cookies.get('role');
    const [loading, setLoading] = useState(true);
    const [dataCompany, setDataCompany] = useState([{ id: 0, Name: "", InternalCode: "", CIF: "", Address: "", Latitude: "", Longitude: "", ContactPerson: "", ContactPhone: "", ContactEmail: "", DefaultLanguage: "", InclusionDate: "" }]);
    const [dataTableVessels, setDataTableVessels] = useState([{ CompanyName: "", VesselName: "", IDVessel: "", YearOfManufacture: "", RegistrationDate: "", VesselSize: "", Photo: "" }]);
    const [dataTableBilling, setDataTableBilling] = useState([{ Bill: "", CompanyName: "", BillingDate: "", Vessel: "", GridPoint: "", Status: "", Status2: "", EnergyConsumption: "", InvoiceAmount: "", Finicio: "", Ffinal: "", Cinicio: "", Cfinal: "" }]);
    const [minDate, setMinDate] = useState("");
    const { t } = useTranslation();
    const uniqueCompanyNames = Array.from(new Set(dataCompany.map(company => company.Name))).sort((a, b) => a.localeCompare(b));
    const [selectedCompany, setSelectedCompany] = useState(uniqueCompanyNames[0]);
    const [filteredDataVessels, setFilteredDataVessels] = useState(dataTableVessels);
    const [dataVesselUnique, setDataVesselUnique] = useState<Vessels | null>(null);
    const [dataStatus, setDataStatus] = useState<Status[] | null>(null);
    const details = dataCompany.find(company => company.Name === selectedCompany);
    const [selectedCompanyDetails, setSelectedCompanyDetails] = useState<{
        id: number;
        Name: string;
        InternalCode: string;
        CIF: string;
        Address: string;
        Latitude: string;
        Longitude: string;
        ContactPerson: string;
        ContactPhone: string;
        ContactEmail: string;
        DefaultLanguage: string;
        InclusionDate: string;
    } | null>(details || null);
    const [loadingBtn, setLoadingBtn] = useState(false);

    /**
     * Formatea una cadena de fecha ISO a formato UTC legible: "YYYY-MM-DD HH:mm:ss".
     * @param isoString - Cadena en formato ISO (por ejemplo: "2025-05-05T14:25:36.000Z").
     * @returns Cadena formateada en formato UTC, por ejemplo: "2025-05-05 14:25:36".
     */
    const formatDate = (isoString: string): string => {
        const timeZone = 'Europe/Madrid';
        return formatInTimeZone(isoString, timeZone, 'yyyy-MM-dd HH:mm:ss');
    };

    /**
     * Obtiene y transforma información desde varias rutas de la API (empresas, embarcaciones, conexiones, estados, constantes).
     * Aplica filtros según `nempresa` y `nembarcacion`, transforma los datos a estructuras necesarias
     * para la vista, actualiza los estados correspondientes y maneja errores.
     * - Aplica transformación y seteo de empresas.
     * - Filtra embarcaciones por empresa seleccionada.
     * - Establece la embarcación específica si `nembarcacion` está definido.
     * - Filtra conexiones completas (con Cfinal) y calcula consumo y factura.
     * - Actualiza las tablas de datos y sus versiones filtradas.
     * @async
     * @function
     * @returns {Promise<void>}
     */
    const getInformation = useCallback(async () => {
        try {

            const responseCompany = await getCompany();

            const responseVessel = await getVessel();

            const responseConnection = await getConnection();

            if (responseConnection.status >= 200 && responseConnection.status < 300) {
                toast.success(`${responseConnection.data.message}`);
            }

            const responseStatus = await getStatus();

            const responseConstant = await getConstant();

            const responseGridPointAll = await getGridPointsAll();

            const filteredStatusConnection = (responseStatus.data.data as Status[]).filter(item => item.Tipo === "Cx" && (item.Nestado === 10 || item.Nestado === 9));
            setDataStatusConnection(filteredStatusConnection);

            setDataGridPointAll(responseGridPointAll.data.data as Grid[]);
            setFilteredDataGridPoint((responseGridPointAll.data.data as Grid[]));

            const filteredCompany = (responseCompany.data.data as Company[]).filter(company => nempresa === '' || company.Nempresa === Number(nempresa))

            const transformedDataCompany = filteredCompany.map(company => ({
                id: company.Nempresa,
                Name: company.Tempresa || "",
                InternalCode: "123",
                CIF: company.CIF || "",
                Address: company.Tdireccion || "",
                Latitude: company.Latitud || "",
                Longitude: company.Longitud || "",
                ContactPerson: company.Tcontacto || "",
                ContactPhone: company.PhoneContacto || "",
                ContactEmail: company.Tcorreo || "",
                DefaultLanguage: company.Lenguaje || "",
                InclusionDate: company.Falta || ""
            }));

            setDataCompany(transformedDataCompany);
            setFilteredDataCompany(transformedDataCompany);

            const uniqueCompanyNames = Array.from(new Set(transformedDataCompany.map(company => company.Name))).sort((a, b) => a.localeCompare(b));
            const details = transformedDataCompany.find(company => company.Name === uniqueCompanyNames[0]) || null;
            setSelectedCompanyDetails(details);
            setSelectedCompany(uniqueCompanyNames[0]);

            const filteredVessel = (responseVessel.data.data as Vessels[]).filter(vessel => nempresa === '' || vessel.Nempresa === Number(nempresa))
            setDataVessel(filteredVessel);
            setFilteredDataVessel(filteredVessel);

            const transformedDataVessel = (responseVessel.data.data as Vessels[]).map(vessel => {
                const companyMatch = (responseCompany.data.data as Company[]).find(item => item.Nempresa === vessel.Nempresa);

                return {
                    VesselName: vessel.Tembarcacion || "",
                    IDVessel: vessel.Nembarcacion.toString() || "",
                    YearOfManufacture: vessel.AManufactura.toString() || "",
                    RegistrationDate: formatDate(vessel.Ffabricacion.toString() || ""),
                    VesselSize: vessel.Tamanno.toString() || "",
                    Photo: vessel.Foto || "",
                    CompanyName: companyMatch ? companyMatch.Tempresa : "",
                };
            });

            setDataTableVessels(transformedDataVessel);
            const filteredDataVessels = transformedDataVessel.filter(item => item.CompanyName === uniqueCompanyNames[0]);
            setFilteredDataVessels(filteredDataVessels);

            if (nembarcacion !== '') {
                const datavessel = (responseVessel.data.data as Vessels[]).filter(vessel => vessel.Nembarcacion === Number(nembarcacion))
                setDataVesselUnique(datavessel.length > 0 ? datavessel[0] : null);
            }

            setDataStatus((responseStatus.data.data as Status[]).length > 0 ? (responseStatus.data.data as Status[]) : null);

            const filteredDataConnection = (responseConnection.data.data as Connection[]).filter(item => item.Cfinal !== null);
            const filteredDataConnection2 = filteredDataConnection.filter(connection => nembarcacion === '' || connection.Nembarcacion === Number(nembarcacion))
            const filteredDataConnection3 = filteredDataConnection2.filter(connection => connection.Status === 9 || connection.Status === 10)
            setFilteredDataConnection(responseConnection.data.data as Connection[]);
            setFilteredDataConnectionID(responseConnection.data.data as Connection[]);

            const transformedDataConnection = filteredDataConnection3.map(connection => {
                const vesselMatch = (responseVessel.data.data as Vessels[]).find(item => item.Nembarcacion === connection.Nembarcacion);
                const companyMatch = (responseCompany.data.data as Company[]).find(item => item.Nempresa === vesselMatch?.Nempresa);
                const statusMatch = (responseStatus.data.data as Status[]).find(item => item.Nestado === connection.Status);
                const constantMatch = (responseConstant.data.data as Constant[]).find(item => item.Nconstante === connection.Nconstante);
                const gridMatch = (responseGridPointAll.data.data as Grid[]).find(item => item.Nmonaguillo === connection.Nmonaguillo);

                return {
                    Bill: `C${connection.Nconexion.toString().padStart(5, '0')}` || "",
                    CompanyName: companyMatch ? companyMatch.Tempresa.toString() : "",
                    BillingDate: formatDate(connection.Ffinal.toString() || ""),
                    Vessel: vesselMatch ? vesselMatch.Tembarcacion.toString() : "",
                    GridPoint: gridMatch ? gridMatch.Tmonaguillo.toString() : "",
                    Status: statusMatch ? statusMatch.Testado.toString() : "",
                    Status2: statusMatch ? statusMatch.Nestado.toString() : "",
                    EnergyConsumption: ((connection.Cfinal! - connection.Cinicio).toFixed(4) || ""),
                    InvoiceAmount: constantMatch?.Valor !== undefined ? ((connection.Cfinal! - connection.Cinicio) * constantMatch.Valor).toFixed(4) : "",
                    Finicio: `${connection.Finicio.toString()}` || "",
                    Ffinal: `${connection.Ffinal.toString()}` || "",
                    Cinicio: `${connection.Cinicio.toString()}` || "",
                    Cfinal: `${connection.Cfinal!.toString()}` || "",
                };
            });

            setDataTableBilling(transformedDataConnection);
            const filteredDataBilling = transformedDataConnection.filter(item => item.CompanyName === uniqueCompanyNames[0]);
            setFilteredDataBilling(filteredDataBilling);
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || error;
            toast.error(`${errorMessage}`);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [nempresa, nembarcacion]);

    /**
     * Hook que se ejecuta al montar el componente.
     * Llama a `getInformation` para cargar datos iniciales y establece la fecha mínima
     * y la fecha de inclusión en formato UTC ISO sin zona horaria.
     */
    useEffect(() => {
        getInformation();

        const nowUTC = new Date().toISOString().slice(0, 19);
        setMinDate(nowUTC);
        setModalInclusionDate(nowUTC);
    }, [getInformation]);

    const [isFullScreenTable, setIsFullScreenTable] = useState(false);
    const [searchTermVessels, setSearchTermVessels] = useState('');
    const [currentPageVessels, setCurrentPageVessels] = useState(1);
    const [itemsPerPageVessels, setItemsPerPageVessels] = useState(10);
    const totalPageVessels = itemsPerPageVessels === filteredDataVessels.length ? 1 : Math.ceil(filteredDataVessels.length / itemsPerPageVessels);
    const startIndexVessels = (currentPageVessels - 1) * itemsPerPageVessels;
    const endIndexVessels = itemsPerPageVessels === filteredDataVessels.length ? filteredDataVessels.length : startIndexVessels + itemsPerPageVessels;
    const currentDataVessels = filteredDataVessels.slice(startIndexVessels, endIndexVessels);
    const ability = useAbility();

    /**
     * Avanza a la siguiente página de la tabla de embarcaciones, si no está en la última.
     */
    const handleNextPageVessels = () => {
        if (currentPageVessels < totalPageVessels) {
            setCurrentPageVessels(currentPageVessels + 1);
        }
    };

    /**
     * Retrocede a la página anterior de la tabla de embarcaciones, si no está en la primera.
     */
    const handlePreviousPageVessels = () => {
        if (currentPageVessels > 1) {
            setCurrentPageVessels(currentPageVessels - 1);
        }
    };

    /**
     * Filtra la tabla de embarcaciones (`dataTableVessels`) según el término de búsqueda.
     * Si hay coincidencias, reinicia a la página 1. Si no hay, muestra página vacía (0).
     */
    const handleSearchVessels = () => {
        const filtered = dataTableVessels.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTermVessels.toLowerCase())
            );
        });
        setFilteredDataVessels(filtered);

        if (filtered.length > 0) {
            setCurrentPageVessels(1);
        } else {
            setCurrentPageVessels(0);
        }
    };

    /**
     * Genera un archivo Excel (`.xlsx`) a partir de los datos de embarcaciones (`dataTableVessels`)
     * y lo descarga con el nombre "Vessels Data.xlsx".
     */
    const downloadDataVesselsAsXLSX = () => {
        const worksheet = XLSX.utils.json_to_sheet(dataTableVessels);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Vessels Data');
        XLSX.writeFile(workbook, 'Vessels Data.xlsx');
    };

    const [sortConfigVessels, setSortConfigVessels] = useState<{
        key: keyof typeof dataTableVessels[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "VesselName", direction: "asc" });

    /**
     * Ordena automáticamente los datos de embarcaciones por nombre al cargar datos.
     */
    useEffect(() => {
        setFilteredDataVessels((prevFilteredData) =>
            [...prevFilteredData].sort((a, b) => a.VesselName.localeCompare(b.VesselName))
        );
    }, [dataTableVessels]);

    /**
     * Ordena la tabla de embarcaciones según la columna proporcionada.
     * Si ya está ordenado ascendente por esa columna, alterna a descendente.
     * @param key - Clave del objeto `dataTableVessels[0]` para ordenar (por ejemplo: "VesselName").
     */
    const sortDataVessels = (key: keyof typeof dataTableVessels[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfigVessels.key === key && sortConfigVessels.direction === "asc") {
            direction = "desc";
        }
        setSortConfigVessels({ key, direction });

        setFilteredDataVessels((prevFilteredData) =>
            [...prevFilteredData].sort((a, b) =>
                direction === "asc" ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key])
            )
        );
    };

    const [modalNameNew, setModalNameNew] = useState('');
    const [modalCIFNew, setModalCIFNew] = useState('');
    const [modalAddressNew, setModalAddressNew] = useState('');
    const [modalLatitudeNew, setModalLatitudeNew] = useState('');
    const [modalLongitudeNew, setModalLongitudeNew] = useState('');
    const [modalNamePersonChargeNew, setModalNamePersonChargeNew] = useState('');
    const [modalPhoneNumberNew, setModalPhoneNumberNew] = useState('');
    const [modalPhoneExtensionNew, setModalPhoneExtensionNew] = useState('');
    const [modalCountryCodeNew, setModalCountryCodeNew] = useState<string>("+");
    const [modalEmailPersonChargeNew, setModalEmailPersonChargeNew] = useState('');
    const [modalCompanyLanguageNew, setModalCompanyLanguageNew] = useState('');
    const [modalInclusionDateNew, setModalInclusionDateNew] = useState('');

    const [showModalNew, setShowModalNew] = useState(false);
    const [modalName, setModalName] = useState('');
    const [modalCIF, setModalCIF] = useState('');
    const [modalAddress, setModalAddress] = useState('');
    const [modalLatitude, setModalLatitude] = useState('');
    const [modalLongitude, setModalLongitude] = useState('');
    const [modalNamePersonCharge, setModalNamePersonCharge] = useState('');
    const [modalPhoneNumber, setModalPhoneNumber] = useState('');
    const [modalPhoneExtension, setModalPhoneExtension] = useState('');
    const [modalCountryCode, setModalCountryCode] = useState<string>("+");
    const [modalEmailPersonCharge, setModalEmailPersonCharge] = useState('');
    const [modalCompanyLanguage, setModalCompanyLanguage] = useState('');
    const [modalInclusionDate, setModalInclusionDate] = useState('');

    /**
     * Abre el modal para crear una nueva empresa.
     */
    const handleCardClickNewCompany = () => {
        setShowModalNew(true);
    };

    /**
     * Cierra todos los modales abiertos y reinicia los estados relacionados con los formularios.
     * Limpia campos de entrada, errores de validación y estados de carga.
     * 
     * Esta función se utiliza para restablecer por completo los valores y errores cuando se cierra 
     * un modal de creación, edición o eliminación, tanto para empresas como para datos de facturación u otros formularios relacionados.
     */
    const closeModal = () => {
        setShowModalNew(false);
        setShowModalEdit(false);
        setShowModalDelete(false);
        setShowModalBillingNew(false);
        setShowModalBillingEdit(false);
        setSelectedName('');
        setModalName("");
        setModalCIF("");
        setModalAddress("");
        setModalLatitude("");
        setModalLongitude("");
        setModalNamePersonCharge("");
        setModalCountryCode("");
        setModalPhoneNumber("");
        setModalPhoneExtension("");
        setModalEmailPersonCharge("");
        setModalCompanyLanguage("");
        setModalInclusionDate("");
        setModalInclusionDate(minDate);
        setErrorCustomerName('');
        setErrorCIF('');
        setErrorActualAddress('');
        setErrorLocationLatitude('');
        setErrorLocationLongitude('');
        setErrorNameOfThePersonInCharge('');
        setErrorPhoneNumberOfThePersonInCharge('');
        setErrorEmailOfThePersonInCharge('');
        setErrorCompanysDefaultLanguage('');
        setErrorInclusionDate('');
        setLoadingBtn(false);
        setErrorVessel('');
        setErrorGridPoint('');
        setErrorStartDate('');
        setErrorFinalDate('');
        setErrorInitialConsumption('');
        setErrorFinalConsumption('');
    };

    /**
     * Cierra el modal al hacer clic en el fondo (overlay).
     * También resetea todos los campos del formulario de nueva empresa.
     * @param e - Evento de clic sobre el overlay del modal.
     */
    const handleOverlayClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).classList.contains('modalOverlay')) {
            closeModal();
        }
    };

    /**
     * Extrae un mensaje de error desde un campo que puede tener diferentes estructuras.
     * 
     * - Si el campo es un arreglo, devuelve el primer elemento (común en respuestas de validación tipo Django REST).
     * - Si el campo es un string, lo retorna directamente.
     * - Si no es ninguna de las anteriores, retorna una cadena vacía.
     * 
     * @param field - Campo de error que puede ser un string, un array de strings o cualquier otro tipo.
     * @returns El mensaje de error como string, o una cadena vacía si no se puede determinar.
     */
    const extractErrorMessage = (field: any) => {
        if (Array.isArray(field)) return field[0];
        if (typeof field === 'string') return field;
        return '';
    };

    /**
     * Valida los campos del formulario y crea una nueva empresa mediante una petición POST.
     * Realiza validación de longitud y rango para latitud/longitud y muestra mensajes con `toast`.
     * Tras agregar, actualiza la información y reinicia el modal.
     */
    const handleAddCompany = async () => {

        setLoadingBtn(true);

        if (!modalNameNew || !modalCIFNew || !modalCompanyLanguageNew || !modalInclusionDateNew) {
            setErrorCustomerName(!modalNameNew ? "Por favor complete este campo" : "");
            setErrorCIF(!modalCIFNew ? "Por favor complete este campo" : "");
            setErrorCompanysDefaultLanguage(!modalCompanyLanguageNew ? "Por favor complete este campo" : "");
            setErrorInclusionDate(!modalInclusionDateNew ? "Por favor complete este campo" : "");
            toast.error(t('company.pleaseCompleteAllFields'));
            setLoadingBtn(false);
            return;
        }

        const lat = modalLatitudeNew ? parseFloat(modalLatitudeNew) : null;
        const lon = modalLongitudeNew ? parseFloat(modalLongitudeNew) : null;

        if (lat !== null) {
            if (lat < -90 || lat > 90) {
                setErrorLocationLatitude(t('company.theLatitudeMustBeBetween90And90Degrees'))
                toast.error(`${t('company.theLatitudeMustBeBetween90And90Degrees')}.`);
                setLoadingBtn(false);
                return;
            }

            const latDecimalPlaces = modalLatitudeNew.split('.')[1]?.length || 0;
            if (latDecimalPlaces < 6) {
                setErrorLocationLatitude(t('company.theLatitudeMustHaveAMinimumOf6DecimalPlaces'))
                toast.error(`${t('company.theLatitudeMustHaveAMinimumOf6DecimalPlaces')}.`);
                setLoadingBtn(false);
                return;
            }
        }

        if (lon !== null) {
            if (lon < -180 || lon > 180) {
                setErrorLocationLongitude(t('company.theLongitudeMustBeBetween180And180Degrees'))
                toast.error(`${t('company.theLongitudeMustBeBetween180And180Degrees')}.`);
                setLoadingBtn(false);
                return;
            }

            const lonDecimalPlaces = modalLongitudeNew.split('.')[1]?.length || 0;
            if (lonDecimalPlaces < 6) {
                setErrorLocationLongitude(t('company.theLengthMustHaveAMinimumOf6DecimalPlaces'))
                toast.error(`${t('company.theLengthMustHaveAMinimumOf6DecimalPlaces')}.`);
                setLoadingBtn(false);
                return;
            }
        }

        let number;

        if (!modalPhoneExtensionNew && modalPhoneNumberNew) {
            number = modalCountryCodeNew + " " + modalPhoneNumberNew
        } else if (modalPhoneNumberNew && modalPhoneExtensionNew) {
            number = modalCountryCodeNew + " " + modalPhoneNumberNew + "ext" + modalPhoneExtensionNew
        } else {
            number = ""
        }

        try {
            const body = {
                Tempresa: modalNameNew,
                Tdireccion: modalAddressNew ? modalAddressNew : "",
                CIF: modalCIFNew,
                Tcontacto: modalNamePersonChargeNew ? modalNamePersonChargeNew : "",
                PhoneContacto: number ? number : "",
                Falta: modalInclusionDateNew,
                Tcorreo: modalEmailPersonChargeNew ? modalEmailPersonChargeNew : "",
                Latitud: modalLatitudeNew ? parseFloat(modalLatitudeNew) : 0,
                Longitud: modalLongitudeNew ? parseFloat(modalLongitudeNew) : 0,
                Lenguaje: modalCompanyLanguageNew
            };

            const response = await postCompany(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('company.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('company.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorCustomerName(extractErrorMessage(errorData.Tempresa));
            setErrorCIF(extractErrorMessage(errorData.CIF));
            setErrorActualAddress(extractErrorMessage(errorData.Tdireccion));
            setErrorLocationLatitude(extractErrorMessage(errorData.Latitud));
            setErrorLocationLongitude(extractErrorMessage(errorData.Longitud));
            setErrorNameOfThePersonInCharge(extractErrorMessage(errorData.Tcontacto));
            setErrorPhoneNumberOfThePersonInCharge(extractErrorMessage(errorData.PhoneContacto));
            setErrorEmailOfThePersonInCharge(extractErrorMessage(errorData.Tcorreo));
            setErrorCompanysDefaultLanguage(extractErrorMessage(errorData.Lenguaje));
            setErrorInclusionDate(extractErrorMessage(errorData.Fcreacion));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    /**
     * Cambia el valor seleccionado de empresa en el `select`.
     * @param event - Evento de cambio del selector.
     */
    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedCompany(event.target.value);
    };

    /**
     * Obtiene la ubicación actual del navegador y la establece en los campos
     * de latitud y longitud del formulario, si el navegador lo permite.
     */
    const handleGetLocation = useCallback(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setModalLatitude(position.coords.latitude.toString());
                    setModalLongitude(position.coords.longitude.toString());
                },
                (error) => {
                    console.log(`${t('company.errorGettingLocation')}: ${error.message}`);
                }
            );
        } else {
            toast.error(`${t('company.geolocationIsNotSupportedByYourBrowser')}.`);
        }
    }, [t]);

    /**
     * Hook que obtiene la ubicación actual cuando se abre el modal de nueva empresa.
     */
    useEffect(() => {
        handleGetLocation();
    }, [showModalNew, handleGetLocation]);

    const [showModalEdit, setShowModalEdit] = useState(false);
    const [filteredDataCompany, setFilteredDataCompany] = useState(dataCompany);
    const [selectedName, setSelectedName] = useState('');

    /**
     * Abre el modal de edición de empresa.
     * También limpia el campo de fecha de inclusión para evitar valores previos.
     */
    const handleCardClickEditCompany = () => {
        setModalInclusionDate("");
        setShowModalEdit(true);
    };

    /**
     * Valida y actualiza la información de una empresa existente.
     * Realiza una petición PATCH con los nuevos datos y muestra mensajes con `toast`.
     */
    const handleUpdateCompany = async () => {
        if (!selectedName) return;

        setLoadingBtn(true);

        if (!modalName || !modalCIF || !modalCompanyLanguage || !modalInclusionDate) {
            setErrorCustomerName(!modalName ? "Por favor complete este campo" : "");
            setErrorCIF(!modalCIF ? "Por favor complete este campo" : "");
            setErrorCompanysDefaultLanguage(!modalCompanyLanguage ? "Por favor complete este campo" : "");
            setErrorInclusionDate(!modalInclusionDate ? "Por favor complete este campo" : "");
            toast.error(t('company.pleaseCompleteAllFields'));
            setLoadingBtn(false);
            return;
        }

        const lat = modalLatitude ? parseFloat(modalLatitude) : null;
        const lon = modalLongitude ? parseFloat(modalLongitude) : null;

        if (lat !== null) {
            if (lat < -90 || lat > 90) {
                setErrorLocationLatitude(t('company.theLatitudeMustBeBetween90And90Degrees'))
                toast.error(`${t('company.theLatitudeMustBeBetween90And90Degrees')}.`);
                setLoadingBtn(false);
                return;
            }

            const latDecimalPlaces = modalLatitude.toString().split('.')[1]?.length || 0;
            if (latDecimalPlaces < 6) {
                setErrorLocationLatitude(t('company.theLatitudeMustHaveAMinimumOf6DecimalPlaces'))
                toast.error(`${t('company.theLatitudeMustHaveAMinimumOf6DecimalPlaces')}.`);
                setLoadingBtn(false);
                return;
            }
        }

        if (lon !== null) {
            if (lon < -180 || lon > 180) {
                setErrorLocationLongitude(t('company.theLongitudeMustBeBetween180And180Degrees'))
                toast.error(`${t('company.theLongitudeMustBeBetween180And180Degrees')}.`);
                setLoadingBtn(false);
                return;
            }

            const lonDecimalPlaces = modalLongitude.toString().split('.')[1]?.length || 0;
            if (lonDecimalPlaces < 6) {
                setErrorLocationLongitude(t('company.theLengthMustHaveAMinimumOf6DecimalPlaces'))
                toast.error(`${t('company.theLengthMustHaveAMinimumOf6DecimalPlaces')}.`);
                setLoadingBtn(false);
                return;
            }
        }

        let id;
        const selectedCompany = dataCompany.find((item) => item.Name === selectedName);
        if (selectedCompany) {
            id = selectedCompany.id;
        } else {
            toast.error(`${t('company.companyNotFound')}`);
            setLoadingBtn(false);
            return;
        }

        let number;

        if (!modalPhoneExtension && modalPhoneNumber) {
            number = modalCountryCode + " " + modalPhoneNumber
        } else if (modalPhoneNumber && modalPhoneExtension) {
            number = modalCountryCode + " " + modalPhoneNumber + "ext" + modalPhoneExtension
        } else {
            number = ""
        }

        try {
            const body = {
                id: id,
                Tempresa: modalName,
                Tdireccion: modalAddress ? modalAddress : "",
                CIF: modalCIF,
                Tcontacto: modalNamePersonCharge ? modalNamePersonCharge : "",
                PhoneContacto: number ? number : "",
                Falta: modalInclusionDate,
                Tcorreo: modalEmailPersonCharge ? modalEmailPersonCharge : "",
                Latitud: modalLatitude ? parseFloat(modalLatitude) : 0,
                Longitud: modalLongitude ? parseFloat(modalLongitude) : 0,
                Lenguaje: modalCompanyLanguage
            };

            const response = await patchCompany(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('company.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('company.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorCustomerName(extractErrorMessage(errorData.Tempresa));
            setErrorCIF(extractErrorMessage(errorData.CIF));
            setErrorActualAddress(extractErrorMessage(errorData.Tdireccion));
            setErrorLocationLatitude(extractErrorMessage(errorData.Latitud));
            setErrorLocationLongitude(extractErrorMessage(errorData.Longitud));
            setErrorNameOfThePersonInCharge(extractErrorMessage(errorData.Tcontacto));
            setErrorPhoneNumberOfThePersonInCharge(extractErrorMessage(errorData.PhoneContacto));
            setErrorEmailOfThePersonInCharge(extractErrorMessage(errorData.Tcorreo));
            setErrorCompanysDefaultLanguage(extractErrorMessage(errorData.Lenguaje));
            setErrorInclusionDate(extractErrorMessage(errorData.Fcreacion));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [showModalDelete, setShowModalDelete] = useState(false);

    /**
     * Abre el modal para confirmar la eliminación de una empresa.
     * Limpia la fecha de inclusión antes de mostrar el modal.
     */
    const handleCardClickDelete = () => {
        setModalInclusionDate("");
        setShowModalDelete(true);
    };

    /**
     * Elimina una empresa usando una petición DELETE al backend.
     * Actualiza la lista de empresas localmente y muestra notificaciones según el resultado.
     */
    const handleDeleteCompany = async () => {
        if (!selectedName) return;

        setLoadingBtn(true);

        let id;
        const selectedCompany = dataCompany.find((item) => item.Name === selectedName);
        if (selectedCompany) {
            id = selectedCompany.id;
        } else {
            toast.error(`${t('company.companyNotFound')}`);
            setLoadingBtn(false);
            return;
        }

        setLoading(true);

        try {
            const data = {
                id: id
            };

            const response = await deleteCompany(data);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                getInformation();
                closeModal();
            } else {
                toast.success(`${t('company.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('company.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorCustomerName(extractErrorMessage(errorData.Tempresa));
            setErrorCIF(extractErrorMessage(errorData.CIF));
            setErrorActualAddress(extractErrorMessage(errorData.Tdireccion));
            setErrorLocationLatitude(extractErrorMessage(errorData.Latitud));
            setErrorLocationLongitude(extractErrorMessage(errorData.Longitud));
            setErrorNameOfThePersonInCharge(extractErrorMessage(errorData.Tcontacto));
            setErrorPhoneNumberOfThePersonInCharge(extractErrorMessage(errorData.PhoneContacto));
            setErrorEmailOfThePersonInCharge(extractErrorMessage(errorData.Tcorreo));
            setErrorCompanysDefaultLanguage(extractErrorMessage(errorData.Lenguaje));
            setErrorInclusionDate(extractErrorMessage(errorData.Fcreacion));

            toast.error(`${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [filteredDataBilling, setFilteredDataBilling] = useState(dataTableBilling);
    const [searchTermBilling, setSearchTermBilling] = useState('');
    const [currentPageBilling, setCurrentPageBilling] = useState(1);
    const [itemsPerPageBilling, setItemsPerPageBilling] = useState(10);
    const totalPageBilling = itemsPerPageBilling === filteredDataBilling.length ? 1 : Math.ceil(filteredDataBilling.length / itemsPerPageBilling);
    const startIndexBilling = (currentPageBilling - 1) * itemsPerPageBilling;
    const endIndexBilling = itemsPerPageBilling === filteredDataBilling.length ? filteredDataBilling.length : startIndexBilling + itemsPerPageBilling;
    const currentDataBilling = filteredDataBilling.slice(startIndexBilling, endIndexBilling);

    /**
     * Cambia a la siguiente página de la tabla de facturación, si no es la última.
     */
    const handleNextPageBilling = () => {
        if (currentPageBilling < totalPageBilling) {
            setCurrentPageBilling(currentPageBilling + 1);
        }
    };

    /**
     * Cambia a la página anterior de la tabla de facturación, si no es la primera.
     */
    const handlePreviousPageBilling = () => {
        if (currentPageBilling > 1) {
            setCurrentPageBilling(currentPageBilling - 1);
        }
    };

    /**
     * Filtra los datos de facturación basándose en el término de búsqueda actual.
     * Si se encuentran resultados, se reinicia a la página 1; si no, a la página 0.
     */
    const handleSearchBilling = () => {
        const filtered = dataTableBilling.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTermBilling.toLowerCase())
            );
        });
        setFilteredDataBilling(filtered);

        if (filtered.length > 0) {
            setCurrentPageBilling(1);
        } else {
            setCurrentPageBilling(0);
        }
    };

    /**
     * Exporta todos los datos de facturación (`dataTableBilling`) a un archivo Excel.
     */
    const downloadDataBillingAsXLSX = () => {
        const sanitizedData = dataTableBilling.map(({ BillingDate, InvoiceAmount, Status, Status2, ...rest }) => rest);
        const renamedData = sanitizedData.map(item => ({
            'Id conexion': item.Bill,
            'Empresa': item.CompanyName,
            'Buque': item.Vessel,
            'Punto de cargar': item.GridPoint,
            'Consumo': item.EnergyConsumption,
            'Fecha inicio': formatDate(item.Finicio),
            'Fecha final': formatDate(item.Ffinal),
            'Lectura inicio': item.Cinicio,
            'Lectura final': item.Cfinal,
        }));
        const worksheet = XLSX.utils.json_to_sheet(renamedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Billing Data');
        XLSX.writeFile(workbook, 'Billing Data.xlsx');
    };

    const [sortConfigBilling, setSortConfigBilling] = useState<{
        key: keyof typeof dataTableBilling[0] | null;
        direction: "asc" | "desc" | null;
    }>({ key: "BillingDate", direction: "desc" });

    /**
     * Ordena los datos de facturación por fecha (de forma descendente) al cambiar `dataTableBilling`.
     */
    useEffect(() => {
        setFilteredDataBilling([...dataTableBilling].sort((a, b) => b.BillingDate.localeCompare(a.BillingDate)));
    }, [dataTableBilling]);

    /**
     * Ordena los datos de facturación según la clave proporcionada.
     * @param key - Clave del objeto por la que se desea ordenar (`BillingDate`, `Amount`, etc.).
     */
    const sortDataBilling = (key: keyof typeof dataTableBilling[0]) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfigBilling.key === key && sortConfigBilling.direction === "asc") {
            direction = "desc";
        }
        setSortConfigBilling({ key, direction });

        const sortedData = [...dataTableBilling].sort((a, b) => {
            if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
            if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
            return 0;
        });

        setFilteredDataBilling(sortedData);
    };

    /**
     * Busca los detalles de la empresa seleccionada (`selectedCompany`)
     * dentro de la lista `dataCompany` y los guarda en `selectedCompanyDetails`.
     */
    const handleCompanySelection = useCallback(() => {
        const details = dataCompany.find(company => company.Name === selectedCompany);
        setSelectedCompanyDetails(details || null);
    }, [dataCompany, selectedCompany]);

    /**
     * Filtra las embarcaciones y facturación por empresa seleccionada.
     * Luego ordena ambas listas según sus configuraciones respectivas de ordenamiento.
     * También ejecuta la función `handleCompanySelection` al final.
     */
    useEffect(() => {
        const filteredDataVesselsTemp = dataTableVessels.filter(item => item.CompanyName === (selectedCompany || uniqueCompanyNames[0]));
        const filteredDataBillingTemp = dataTableBilling.filter(item => item.CompanyName === (selectedCompany || uniqueCompanyNames[0]));

        const sortedFilteredDataVessels = [...filteredDataVesselsTemp].sort((a, b) => {
            if (sortConfigVessels.direction === "asc") {
                return a[sortConfigVessels.key!].localeCompare(b[sortConfigVessels.key!]);
            } else {
                return b[sortConfigVessels.key!].localeCompare(a[sortConfigVessels.key!]);
            }
        });

        const filteredVessels = sortedFilteredDataVessels.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTermVessels.toLowerCase())
            );
        });

        const sortedFilteredDataBilling = [...filteredDataBillingTemp].sort((a, b) => {
            if (sortConfigBilling.direction === "asc") {
                return a[sortConfigBilling.key!].localeCompare(b[sortConfigBilling.key!]);
            } else {
                return b[sortConfigBilling.key!].localeCompare(a[sortConfigBilling.key!]);
            }
        });

        const filteredBilling = sortedFilteredDataBilling.filter((item) => {
            return Object.values(item).some(value =>
                value.toString().toLowerCase().includes(searchTermBilling.toLowerCase())
            );
        });

        setFilteredDataVessels(filteredVessels);
        setFilteredDataBilling(filteredBilling);

        handleCompanySelection();
    }, [selectedCompany]);

    const [isSelectOpenName, setIsSelectOpenName] = useState(false);
    const handleSelectFocusName = () => setIsSelectOpenName(true);
    const handleSelectBlurName = () => setIsSelectOpenName(false);

    /**
     * Limpia el código del país (`modalCountryCode`) eliminando espacios y dejando solo el primer fragmento,
     * cada vez que cambian `modalName` o `modalCountryCode`.
     */
    useEffect(() => {
        if (modalName && modalCountryCode) {
            const cleanValue = modalCountryCode.trim().split(" ")[0];
            setModalCountryCode(cleanValue);
        }
    }, [modalName, modalCountryCode]);

    const [showModalBillingNew, setShowModalBillingNew] = useState(false);
    const [showModalBillingEdit, setShowModalBillingEdit] = useState(false);
    const [modalVesselNew, setModalVesselNew] = useState('');
    const [modalGridPointNew, setModalGridPointNew] = useState('');
    const [modalDateInicialNew, setModalDateInicialNew] = useState('');
    const [modalDateFinalNew, setModalDateFinalNew] = useState('');
    const [modalInitialConsumptionNew, setModalInitialConsumptionNew] = useState('');
    const [modalFinalConsumptionNew, setModalFinalConsumptionNew] = useState('');
    const [dataVessel, setDataVessel] = useState<Vessels[]>([]);
    const [filteredDataVessel, setFilteredDataVessel] = useState(dataVessel);
    const [dataGridPointAll, setDataGridPointAll] = useState<Grid[]>([]);
    const [filteredDataGridPoint, setFilteredDataGridPoint] = useState(dataGridPointAll);
    const [dataStatusConnection, setDataStatusConnection] = useState<Status[]>([]);
    const [modalID, setModalID] = useState('');
    const [filteredDataConnection, setFilteredDataConnection] = useState<Connection[]>([]);
    const [filteredDataConnectionID, setFilteredDataConnectionID] = useState(filteredDataConnection);

    const [modalVessel, setModalVessel] = useState('');
    const [modalGridPoint, setModalGridPoint] = useState('');
    const [modalDateInicial, setModalDateInicial] = useState('');
    const [modalDateFinal, setModalDateFinal] = useState('');
    const [modalInitialConsumption, setModalInitialConsumption] = useState('');
    const [modalFinalConsumption, setModalFinalConsumption] = useState('');
    const [modalStatus, setModalStatus] = useState('');

    const [isSelectOpenVessel, setIsSelectOpenVessel] = useState(false);
    const handleSelectFocusVessel = () => setIsSelectOpenVessel(true);
    const handleSelectBlurVessel = () => setIsSelectOpenVessel(false);

    const [isSelectOpenGridPoint, setIsSelectOpenGridPoint] = useState(false);
    const handleSelectFocusGridPoint = () => setIsSelectOpenGridPoint(true);
    const handleSelectBlurGridPoint = () => setIsSelectOpenGridPoint(false);

    const [isSelectOpenStatus, setIsSelectOpenStatus] = useState(false);
    const handleSelectFocusStatus = () => setIsSelectOpenStatus(true);
    const handleSelectBlurStatus = () => setIsSelectOpenStatus(false);

    const [isSelectOpenID, setIsSelectOpenID] = useState(false);
    const handleSelectFocusID = () => setIsSelectOpenID(true);
    const handleSelectBlurID = () => setIsSelectOpenID(false);

    /**
     * Maneja el evento de clic en una tarjeta (card).
     * 
     * Al activarse, muestra el modal para crear un nuevo registro de facturación.
     */
    const handleCardClick = () => {
        setShowModalBillingNew(true);
    };

    /**
     * Crea una nueva conexión con los datos ingresados en el formulario y realiza la solicitud para guardarla en el servidor.
     * Valida los campos requeridos y muestra mensajes de error si no se cumplen las condiciones.
     */
    const handleCreateConnection = async () => {

        setLoadingBtn(true);

        if (!modalVesselNew || !modalGridPointNew || !modalDateInicialNew || !modalDateFinalNew || !modalInitialConsumptionNew || !modalFinalConsumptionNew) {
            setErrorVessel(!modalVesselNew ? "Por favor complete este campo" : "");
            setErrorGridPoint(!modalGridPointNew ? "Por favor complete este campo" : "");
            setErrorStartDate(!modalDateInicialNew ? "Por favor complete este campo" : "");
            setErrorFinalDate(!modalDateFinalNew ? "Por favor complete este campo" : "");
            setErrorInitialConsumption(!modalInitialConsumptionNew ? "Por favor complete este campo" : "");
            setErrorFinalConsumption(!modalFinalConsumptionNew ? "Por favor complete este campo" : "");
            toast.error(`${t('registers.pleaseCompleteAllFields')}`);
            setLoadingBtn(false);
            return;
        }

        const finalValue = parseFloat(modalFinalConsumptionNew);
        const initialValue = parseFloat(modalInitialConsumptionNew);

        if (finalValue < initialValue) {
            setErrorFinalConsumption(t('registers.finalConsumptionIsLowerThanInitialConsumption'));
            toast.error(`${t('registers.finalConsumptionIsLowerThanInitialConsumption')}`);
            setLoadingBtn(false);
            return;
        }

        try {
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVesselNew);
            const gridPointMatch = dataGridPointAll.find(item => item.Tmonaguillo === modalGridPointNew);
            const statusMatch = dataStatusConnection.find(item => item.Nestado === 9);

            const body = {
                Nembarcacion: vesselMatch?.Nembarcacion,
                Nmonaguillo: gridPointMatch?.Nmonaguillo,
                Finicio: modalDateInicialNew,
                Ffinal: modalDateFinalNew,
                Cinicio: Number(modalInitialConsumptionNew),
                Cfinal: Number(modalFinalConsumptionNew),
                Status: statusMatch?.Nestado,
                Nconstante: 1,
            };

            const response = await postConnection(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('registers.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('registers.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorVessel(extractErrorMessage(errorData.Nembarcacion));
            setErrorGridPoint(extractErrorMessage(errorData.Nmonaguillo));
            setErrorStartDate(extractErrorMessage(errorData.Finicio));
            setErrorFinalDate(extractErrorMessage(errorData.Ffinal));
            setErrorInitialConsumption(extractErrorMessage(errorData.Cinicio));
            setErrorFinalConsumption(extractErrorMessage(errorData.Cfinal));

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    /**
     * Actualiza una conexión existente con los datos del modal. Si los campos son válidos,
     * se realiza la solicitud para actualizar la conexión en el servidor.
     */
    const handleUpdateConnection = async () => {

        setLoadingBtn(true);

        if (!modalVessel || !modalGridPoint || !modalDateInicial || !modalDateFinal || !modalInitialConsumption || !modalFinalConsumption || !modalStatus) {
            setErrorVessel(!modalVessel ? "Por favor complete este campo" : "");
            setErrorGridPoint(!modalGridPoint ? "Por favor complete este campo" : "");
            setErrorStartDate(!modalDateInicial ? "Por favor complete este campo" : "");
            setErrorFinalDate(!modalDateFinal ? "Por favor complete este campo" : "");
            setErrorInitialConsumption(!modalInitialConsumption ? "Por favor complete este campo" : "");
            setErrorFinalConsumption(!modalFinalConsumption ? "Por favor complete este campo" : "");
            toast.error(`${t('registers.pleaseCompleteAllFields')}`);
            setLoadingBtn(false);
            return;
        }

        const finalValue = parseFloat(modalFinalConsumption);
        const initialValue = parseFloat(modalInitialConsumption);

        if (finalValue < initialValue) {
            setErrorFinalConsumption(t('registers.finalConsumptionIsLowerThanInitialConsumption'));
            toast.error(`${t('registers.finalConsumptionIsLowerThanInitialConsumption')}`);
            setLoadingBtn(false);
            return;
        }

        try {
            const vesselMatch = dataVessel.find(item => item.Tembarcacion === modalVessel);
            const gridPointMatch = dataGridPointAll.find(item => item.Tmonaguillo === modalGridPoint);
            const statusMatch = dataStatusConnection.find(item => item.Testado === modalStatus);

            const body = {
                id: modalID,
                Nembarcacion: vesselMatch?.Nembarcacion,
                Nmonaguillo: gridPointMatch?.Nmonaguillo,
                Finicio: modalDateInicial,
                Ffinal: modalDateFinal,
                Cinicio: Number(modalInitialConsumption),
                Cfinal: Number(modalFinalConsumption),
                Status: statusMatch?.Nestado,
                Nconstante: 1,
            };

            const response = await patchConnection(body);

            if (response.status >= 200 && response.status < 300) {
                toast.success(`${response.data.message}`);
                setLoading(true);
                getInformation();
                closeModal();
            } else {
                toast.error(`${t('registers.theResponseWasNotSuccessful')}: ${response.status}`);
                setLoading(false);
                setLoadingBtn(false);
            }

        } catch (error: any) {
            let message = `${t('registers.errorMakingTheRequest')}: ${error}`;

            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data;

                if (typeof data === 'string') {
                    message = data;
                } else if (typeof data === 'object') {
                    const firstKey = Object.keys(data)[0];
                    const value = data[firstKey];

                    if (Array.isArray(value)) {
                        message = value[0];
                    } else {
                        message = value;
                    }
                }
            } else if (error instanceof Error) {
                message = error.message;
            }

            const errorData = error?.response?.data?.error || {};
            setErrorVessel(extractErrorMessage(errorData.Nembarcacion));
            setErrorGridPoint(extractErrorMessage(errorData.Nmonaguillo));
            setErrorStartDate(extractErrorMessage(errorData.Finicio));
            setErrorFinalDate(extractErrorMessage(errorData.Ffinal));
            setErrorInitialConsumption(extractErrorMessage(errorData.Cinicio));
            setErrorFinalConsumption(extractErrorMessage(errorData.Cfinal));

            toast.error(`Error: ${message}`, {
                position: "top-right"
            });

            setLoading(false);
            setLoadingBtn(false);
        }
    };

    const [errorCustomerName, setErrorCustomerName] = useState('');
    const [errorCIF, setErrorCIF] = useState('');
    const [errorActualAddress, setErrorActualAddress] = useState('');
    const [errorLocationLatitude, setErrorLocationLatitude] = useState('');
    const [errorLocationLongitude, setErrorLocationLongitude] = useState('');
    const [errorNameOfThePersonInCharge, setErrorNameOfThePersonInCharge] = useState('');
    const [errorPhoneNumberOfThePersonInCharge, setErrorPhoneNumberOfThePersonInCharge] = useState('');
    const [errorEmailOfThePersonInCharge, setErrorEmailOfThePersonInCharge] = useState('');
    const [errorCompanysDefaultLanguage, setErrorCompanysDefaultLanguage] = useState('');
    const [errorInclusionDate, setErrorInclusionDate] = useState('');

    const [errorVessel, setErrorVessel] = useState('');
    const [errorGridPoint, setErrorGridPoint] = useState('');
    const [errorStartDate, setErrorStartDate] = useState('');
    const [errorFinalDate, setErrorFinalDate] = useState('');
    const [errorInitialConsumption, setErrorInitialConsumption] = useState('');
    const [errorFinalConsumption, setErrorFinalConsumption] = useState('');

    const columns: ColumnConfig[] = [
        { key: 'VesselName', label: t('company.vesselName'), sortable: true },
        { key: 'IDVessel', label: t('company.IDVessel'), sortable: true },
        { key: 'YearOfManufacture', label: t('company.yearofManufacture'), sortable: true },
        { key: 'RegistrationDate', label: t('company.registrationDate'), sortable: true },
        { key: 'VesselSize', label: t('company.vesselSize'), sortable: true },
    ];

    useEffect(() => {
        if (showModalNew) {
            if (showModalNew && modalCountryCodeNew === "+") {
                setModalCountryCodeNew("+34");
            } else if (!showModalNew && !modalCountryCode) {
                setModalCountryCode("+34");
            }
        }
    }, [showModalNew]);

    return (
        <div className='containerCompany'>
            {loading ? (
                <div className="loading">
                    <ClipLoader color="#007bff" size={50} />
                    <p>{t('company.loadingInformation')}</p>
                </div>
            ) : (
                <>
                    <div className='title'>
                        {ability.can('read', 'AdminPage') && (
                            <select value={selectedCompany} onChange={handleChange}>
                                {uniqueCompanyNames.map((name, index) => (
                                    <option key={index} value={name}>
                                        {name}
                                    </option>
                                ))}
                            </select>
                        )}
                        {(ability.can('read', 'ManagerPage') || ability.can('read', 'UserPage')) && (
                            <span>{selectedCompany}</span>
                        )}
                        {showResolved === "companyInformation" && (
                            <div>
                                {ability.can('create', 'AdminPage') && (
                                    <button className="btn btn-dark" onClick={handleCardClickNewCompany}>
                                        <i className="fas fa-plus"></i> {t('company.newCompany')}
                                    </button>
                                )}
                                {ability.can('update', 'AdminPage') && (
                                    <button className="btn btn-dark" onClick={handleCardClickEditCompany}>
                                        {t('company.edit')}
                                    </button>
                                )}
                                {ability.can('delete', 'AdminPage') && (
                                    <button className="btn btn-danger" onClick={handleCardClickDelete}>
                                        {t('company.delete')}
                                    </button>
                                )}
                            </div>
                        )}

                        {showResolved === "billing" && (
                            <div>
                                <button className="btn btn-dark" onClick={handleCardClick}>
                                    <i className="fas fa-plus"></i> {t('registers.newConnection')}
                                </button>
                            </div>
                        )}
                    </div>

                    {(ability.can('read', 'AdminPage') || ability.can('read', 'ManagerPage')) && (
                        <div className='buttons' >
                            <button className={`btn ${showResolved === "companyInformation" && 'active'}`} onClick={() => setShowResolved('companyInformation')}>
                                {t('company.companyInformation')}
                            </button>
                            <button className={`btn ${showResolved === "billing" && 'active'}`} onClick={() => setShowResolved('billing')}>
                                {t('company.historical')}
                            </button>
                        </div>
                    )}

                    {showResolved === "companyInformation" && (
                        <>
                            <div className='separator1'>
                                <div className='card'>
                                    <span>{ability.can('read', 'UserPage') ? t('company.vesselDetails') : t('company.companyDetails')}</span>
                                    {(!ability.can('read', 'UserPage')) && (
                                        <>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-building"></i></span>
                                                    <span>{t('company.name')}:</span>
                                                </div>
                                                <span>{selectedCompanyDetails?.Name || t('company.noNameAvailable')}</span>
                                            </div>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-map-marker-alt"></i></span>
                                                    <span>{t('company.address')}:</span>
                                                </div>
                                                <span>{selectedCompanyDetails?.Address || t('company.noAddressAvailable')}</span>
                                            </div>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-envelope"></i></span>
                                                    <span>{t('company.email')}:</span>
                                                </div>
                                                <span>{selectedCompanyDetails?.ContactEmail || t('company.noContactEmailAvailable')}</span>
                                            </div>
                                        </>
                                    )}
                                    {(ability.can('read', 'UserPage')) && (
                                        <>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-ship"></i></span>
                                                    <span>{t('company.name')}:</span>
                                                </div>
                                                <span>{dataVesselUnique?.Tembarcacion || t('company.noNameAvailable')}</span>
                                            </div>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-hashtag"></i></span>
                                                    <span>{t('company.recordId')}:</span>
                                                </div>
                                                <span>{dataVesselUnique?.IDregistro || t('company.noIdRegistroAvailable')}</span>
                                            </div>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-calendar-day"></i></span>
                                                    <span>{t('company.registrationDate')}:</span>
                                                </div>
                                                <span>
                                                    {dataVesselUnique?.Ffabricacion
                                                        ? new Date(dataVesselUnique.Ffabricacion.toString()).toLocaleDateString("es-ES", {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            second: "2-digit",
                                                            timeZone: "UTC",
                                                            hour12: false,
                                                        })
                                                        : t('company.noRegistrationDateAvailable')}
                                                </span>
                                            </div>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-industry"></i></span>
                                                    <span>{t('company.yearOfManufacture')}:</span>
                                                </div>
                                                <span>{dataVesselUnique?.AManufactura || t('company.noYearOfManufactureAvailable')}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className='card'>
                                    <span>{t('company.contactInformation')}</span>
                                    <div>
                                        <div>
                                            <span className='icon'><i className="fa-solid fa-user"></i></span>
                                            <span>{t('company.contactPerson')}:</span>
                                        </div>
                                        {(!ability.can('read', 'UserPage')) && (
                                            <span>{selectedCompanyDetails?.ContactPerson || t('company.noContactPersonAvailable')}</span>
                                        )}
                                        {(ability.can('read', 'UserPage')) && (
                                            <span>{name || t('company.noNameAvailable')}</span>
                                        )}
                                    </div>
                                    <div>
                                        <div>
                                            <span className='icon'><i className="fa-solid fa-phone"></i></span>
                                            <span>{t('company.phone')}:</span>
                                        </div>
                                        {(!ability.can('read', 'UserPage')) && (
                                            <span>{selectedCompanyDetails?.ContactPhone || t('company.noContactPhoneAvailable')}</span>
                                        )}
                                        {(ability.can('read', 'UserPage')) && (
                                            <span>{phone || t('company.noContactPhoneAvailable')}</span>
                                        )}
                                    </div>
                                    {(ability.can('read', 'UserPage')) && (
                                        <div>
                                            <div>
                                                <span className='icon'><i className="fa-solid fa-envelope"></i></span>
                                                <span>{t('company.email')}:</span>
                                            </div>
                                            <span>{email || t('company.noContactEmailAvailable')}</span>
                                        </div>
                                    )}
                                </div>

                                <div className='card'>
                                    <span>{t('company.systemInformation')}</span>

                                    {(!ability.can('read', 'UserPage')) && (
                                        <>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-calendar"></i></span>
                                                    <span>{t('company.inclusionDate')}:</span>
                                                </div>
                                                <span>
                                                    {selectedCompanyDetails?.InclusionDate
                                                        ? new Date(selectedCompanyDetails.InclusionDate).toLocaleDateString("es-ES", {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            second: "2-digit",
                                                            timeZone: "UTC",
                                                            hour12: false,
                                                        })
                                                        : t('company.noInclusionDateAvailable')}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {(ability.can('read', 'UserPage')) && (
                                        <>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-ship"></i></span>
                                                    <span>{t('company.size')}:</span>
                                                </div>
                                                <span>{dataVesselUnique?.Tamanno || t('company.noSizeAvailable')} m</span>
                                            </div>
                                            <div>
                                                <div>
                                                    <span className='icon'><i className="fa-solid fa-hashtag"></i></span>
                                                    <span>{t('company.type')}:</span>
                                                </div>
                                                <span>
                                                    {dataStatus?.find(status => status.Nestado === dataVesselUnique?.Tipo)?.Testado || t('company.noStatusAvailable')}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {(ability.can('read', 'AdminPage') || ability.can('read', 'ManagerPage')) && (
                                <div className={`separator2 ${isFullScreenTable ? 'expanded' : ''}`}>
                                    <Table
                                        t={t}
                                        titleKey="company.vesselsList"
                                        placeholder='company.searchvessels'
                                        searchTerm={searchTermVessels}
                                        setSearchTerm={setSearchTermVessels}
                                        handleSearch={handleSearchVessels}
                                        downloadDataAsXLSX={downloadDataVesselsAsXLSX}
                                        isFullScreen={isFullScreenTable}
                                        setIsFullScreen={setIsFullScreenTable}

                                        nameClassTable="tableVessels"
                                        columns={columns}
                                        currentData={currentDataVessels}
                                        sortConfig={sortConfigVessels}
                                        onSort={sortDataVessels}

                                        handlePreviousPage={handlePreviousPageVessels}
                                        currentPage={currentPageVessels}
                                        totalPage={totalPageVessels}
                                        itemsPerPage={itemsPerPageVessels}
                                        filteredData={dataTableVessels}
                                        setItemsPerPage={setItemsPerPageVessels}
                                        setCurrentPage={setCurrentPageVessels}
                                        handleNextPage={handleNextPageVessels}
                                        data={dataTableVessels}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {(showResolved === "billing" || ability.can('read', 'UserPage')) && (
                        <>
                            <div className={`separator2 ${isFullScreenTable ? 'expanded' : ''}`}>
                                <div className='caption'>
                                    <div className='left'>
                                        <h5>{t('company.connectionData')}</h5>
                                    </div>
                                    <div className='right'>
                                        <button className="btn search-btn" onClick={handleSearchBilling}>
                                            <i className="fas fa-search"></i>
                                        </button>

                                        <input
                                            type="text"
                                            placeholder={t('company.searchBilling') + " ..."}
                                            className="form-control search-input"
                                            value={searchTermBilling}
                                            onChange={(e) => setSearchTermBilling(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSearchBilling();
                                                }
                                            }}
                                        />

                                        <button className="btn btn-dark download-btn" onClick={downloadDataBillingAsXLSX}>
                                            <i className="fas fa-download"></i>
                                        </button>

                                        <button className="btn btn-dark fullscreen-btn" onClick={() => {
                                            setIsFullScreenTable(!isFullScreenTable);
                                        }}>
                                            <i className={`fas ${isFullScreenTable ? 'fa-compress' : 'fa-expand'}`}></i>
                                        </button>
                                    </div>
                                </div>
                                <div className='tableVessels'>
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th onClick={() => sortDataBilling("Bill")}>
                                                    {t('company.connection')} {sortConfigBilling.key === "Bill" ?
                                                        (sortConfigBilling.direction === "asc" ?
                                                            (<i className="fas fa-arrow-up" />) :
                                                            (<i className="fas fa-arrow-down" />)
                                                        ) : (
                                                            <i className="fa-solid fa-arrow-down-a-z"></i>
                                                        )}
                                                </th>
                                                <th onClick={() => sortDataBilling("BillingDate")}>
                                                    {t('company.connectionDate')} {sortConfigBilling.key === "BillingDate" ?
                                                        (sortConfigBilling.direction === "asc" ?
                                                            (<i className="fas fa-arrow-up" />) :
                                                            (<i className="fas fa-arrow-down" />)
                                                        ) : (
                                                            <i className="fa-solid fa-arrow-down-a-z"></i>
                                                        )}
                                                </th>
                                                <th onClick={() => sortDataBilling("Vessel")}>
                                                    {t('company.vessel')} {sortConfigBilling.key === "Vessel" ?
                                                        (sortConfigBilling.direction === "asc" ?
                                                            (<i className="fas fa-arrow-up" />) :
                                                            (<i className="fas fa-arrow-down" />)
                                                        ) : (
                                                            <i className="fa-solid fa-arrow-down-a-z"></i>
                                                        )}
                                                </th>
                                                <th onClick={() => sortDataBilling("GridPoint")}>
                                                    {t('company.gridPoint')} {sortConfigBilling.key === "GridPoint" ?
                                                        (sortConfigBilling.direction === "asc" ?
                                                            (<i className="fas fa-arrow-up" />) :
                                                            (<i className="fas fa-arrow-down" />)
                                                        ) : (
                                                            <i className="fa-solid fa-arrow-down-a-z"></i>
                                                        )}
                                                </th>
                                                <th onClick={() => sortDataBilling("EnergyConsumption")}>
                                                    {t('company.energyConsumption')} (kWh) {sortConfigBilling.key === "EnergyConsumption" ?
                                                        (sortConfigBilling.direction === "asc" ?
                                                            (<i className="fas fa-arrow-up" />) :
                                                            (<i className="fas fa-arrow-down" />)
                                                        ) : (
                                                            <i className="fa-solid fa-arrow-down-a-z"></i>
                                                        )}
                                                </th>
                                                <th>
                                                    {t('registers.actions')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentDataBilling.length > 0 && currentDataBilling[0]?.Vessel ? (
                                                currentDataBilling.map((row, index) => (
                                                    <tr key={index}>
                                                        <td>{row.Bill}</td>
                                                        <td>{row.BillingDate}</td>
                                                        <td>{row.Vessel}</td>
                                                        <td>{row.GridPoint}</td>
                                                        <td>{row.EnergyConsumption}</td>
                                                        <td className='actions'>
                                                            <i onClick={() => {
                                                                setShowModalBillingEdit(true);
                                                                setModalID(Number(row.Bill.replace(/^C/, '')).toString());
                                                                setModalVessel(row.Vessel);
                                                                setModalGridPoint(row.GridPoint);
                                                                setModalDateInicial(row.Finicio);
                                                                setModalDateFinal(row.Ffinal);
                                                                setModalInitialConsumption(row.Cinicio);
                                                                setModalFinalConsumption(row.Cfinal);
                                                                setModalStatus(row.Status);
                                                            }}
                                                                className="fas fa-edit"></i>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} style={{ textAlign: "center" }}>{t('registers.noInformationAvailable')}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="pagination">
                                    <button className="btn btn-secondary" onClick={handlePreviousPageBilling} disabled={currentPageBilling === 1}>
                                        <i className="fas fa-chevron-left"></i> {t('company.previous')}
                                    </button>
                                    <div>
                                        <span>{t('company.page')} {currentPageBilling} {t('company.of')} {totalPageBilling},</span>
                                        <div className="pagination-controls">
                                            <span>{t('company.rowsPerPage')}:</span>
                                            <select
                                                className="form-control rows-select"
                                                value={
                                                    itemsPerPageBilling === filteredDataBilling.length && ![5, 10, 15, 50].includes(itemsPerPageBilling)
                                                        ? "All"
                                                        : itemsPerPageBilling
                                                }
                                                onChange={(e) => {
                                                    const value = e.target.value === "All" ? filteredDataBilling.length : parseInt(e.target.value, 10);
                                                    setItemsPerPageBilling(value);
                                                    setCurrentPageBilling(1);
                                                }}
                                            >
                                                <option value="5">5</option>
                                                <option value="10">10</option>
                                                <option value="15">15</option>
                                                <option value="50">50</option>
                                                <option value="All">{t('company.all')}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleNextPageBilling}
                                        disabled={currentPageBilling === Math.ceil(filteredDataBilling.length / itemsPerPageBilling)}
                                    >
                                        {t('company.next')} <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {(showModalNew || showModalEdit || showModalDelete) && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContent'>
                                <div className='title'>
                                    <h6>
                                        {showModalNew ? t('company.newCompany') : showModalEdit ? t('company.editCompany') : showModalDelete ? t('company.deleteCompany') : ''}
                                    </h6>
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('company.customerName')}</h6>
                                        {showModalNew && (
                                            <input
                                                type="text"
                                                className={`form-control ${errorCustomerName ? 'is-invalid' : ''}`}
                                                value={modalNameNew}
                                                onChange={(e) => {
                                                    setErrorCustomerName('');
                                                    const newName = e.target.value;
                                                    const isDuplicate = dataCompany.some(company => company.Name === newName);

                                                    if (isDuplicate) {
                                                        toast.error(`${t('company.thisNameAlreadyExists')}: "${newName}"`);
                                                    } else {
                                                        setModalNameNew(newName);
                                                    }
                                                }}
                                                placeholder={`${t('company.enterThe1')} ${t('company.customerName').toLowerCase()}`}
                                            />
                                        )}
                                        {!showModalNew && (
                                            <div className="select-wrapper">
                                                <select
                                                    className={`form-control ${errorCustomerName ? 'is-invalid' : ''}`}
                                                    value={modalName}
                                                    onChange={(e) => {
                                                        setModalName(e.target.value);
                                                        setSelectedName(e.target.value);
                                                        const selectedItem = filteredDataCompany.find((data) => data.Name === e.target.value);
                                                        setModalCIF(selectedItem ? selectedItem.CIF : "");
                                                        setModalAddress(selectedItem ? selectedItem.Address : "");
                                                        setModalLatitude(selectedItem ? selectedItem.Latitude : "");
                                                        setModalLongitude(selectedItem ? selectedItem.Longitude : "");
                                                        setModalNamePersonCharge(selectedItem ? selectedItem.ContactPerson : "");
                                                        const firstPart = selectedItem ? selectedItem.ContactPhone.split(' ')[0] : "";
                                                        const extIndex = selectedItem ? selectedItem.ContactPhone.toLowerCase().indexOf("ext") : -1;
                                                        const secondPart = extIndex !== -1 ? selectedItem ? selectedItem.ContactPhone.substring(selectedItem.ContactPhone.indexOf(" ") + 1, selectedItem.ContactPhone.indexOf("ext")).trim() : "" : selectedItem ? selectedItem.ContactPhone.split(' ')[1] : "";
                                                        setModalCountryCode(firstPart + " " + secondPart);
                                                        setModalPhoneNumber(secondPart);
                                                        const thirdPart = extIndex !== -1 ? selectedItem ? selectedItem.ContactPhone.substring(selectedItem.ContactPhone.indexOf("ext") + 3).trim() : "" : "";
                                                        setModalPhoneExtension(thirdPart);
                                                        setModalEmailPersonCharge(selectedItem ? selectedItem.ContactEmail : "");
                                                        setModalCompanyLanguage(selectedItem ? selectedItem.DefaultLanguage : "");
                                                        setModalInclusionDate(selectedItem ? selectedItem.InclusionDate : "");
                                                        setErrorCustomerName('');
                                                    }}
                                                    onFocus={handleSelectFocusName}
                                                    onBlur={handleSelectBlurName}
                                                >
                                                    <option value="">{`${t('company.selectA2')} ${t('company.customerName').toLowerCase()}`} ...</option>
                                                    {filteredDataCompany
                                                        .map((item) => (
                                                            <option key={item.Name} value={String(item.Name)}>
                                                                {item.Name}
                                                            </option>
                                                        ))}
                                                </select>
                                                <span className="icon">
                                                    {isSelectOpenName ? (
                                                        <i className="fas fa-chevron-up"></i>
                                                    ) : (
                                                        <i className="fas fa-chevron-down"></i>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    {errorCustomerName && (
                                        <div className="invalid-feedback d-block">{errorCustomerName}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('company.internalCode')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorCIF ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalCIFNew : modalCIF}
                                            onChange={(e) => {
                                                if (showModalNew) { setModalCIFNew(e.target.value); }
                                                if (!showModalNew) { setModalCIF(e.target.value); }
                                                setErrorCIF('');
                                            }}
                                            placeholder={`${t('company.enterThe1')} ${t('company.internalCode').toLowerCase()}`}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                        />
                                    </div>
                                    {errorCIF && (
                                        <div className="invalid-feedback d-block">{errorCIF}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('company.actualAddress')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorActualAddress ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalAddressNew : modalAddress}
                                            onChange={(e) => {
                                                if (showModalNew) { setModalAddressNew(e.target.value); }
                                                if (!showModalNew) { setModalAddress(e.target.value); }
                                                setErrorActualAddress('');
                                            }}
                                            placeholder={`${t('company.enterThe2')} ${t('company.actualAddress').toLowerCase()}`}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                        />
                                    </div>
                                    {errorActualAddress && (
                                        <div className="invalid-feedback d-block">{errorActualAddress}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('company.locationLatitude')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorLocationLatitude ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalLatitudeNew : modalLatitude}
                                            onChange={(e) => {
                                                if (/^-?\d*\.?\d*$/.test(e.target.value)) {
                                                    if (showModalNew) { setModalLatitudeNew(e.target.value); }
                                                    if (!showModalNew) { setModalLatitude(e.target.value); }
                                                    setErrorLocationLatitude('');
                                                }
                                            }}
                                            placeholder={`Valor entre -90.000000 y 90.000000`}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                        />
                                    </div>
                                    {errorLocationLatitude && (
                                        <div className="invalid-feedback d-block">{errorLocationLatitude}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('company.locationLongitude')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorLocationLongitude ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalLongitudeNew : modalLongitude}
                                            onChange={(e) => {
                                                if (/^-?\d*\.?\d*$/.test(e.target.value)) {
                                                    if (showModalNew) { setModalLongitudeNew(e.target.value); }
                                                    if (!showModalNew) { setModalLongitude(e.target.value); }
                                                    setErrorLocationLongitude('');
                                                }
                                            }}
                                            placeholder={`Valor entre -180.000000 y 180.000000`}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                        />
                                    </div>
                                    {errorLocationLongitude && (
                                        <div className="invalid-feedback d-block">{errorLocationLongitude}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('company.nameOfThePersonInCharge')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorNameOfThePersonInCharge ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalNamePersonChargeNew : modalNamePersonCharge}
                                            onChange={(e) => {
                                                if (showModalNew) { setModalNamePersonChargeNew(e.target.value); }
                                                if (!showModalNew) { setModalNamePersonCharge(e.target.value); }
                                                setErrorNameOfThePersonInCharge('');
                                            }}
                                            placeholder={`${t('company.enterThe1')} ${t('company.nameOfThePersonInCharge').toLowerCase()}`}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                        />
                                    </div>
                                    {errorNameOfThePersonInCharge && (
                                        <div className="invalid-feedback d-block">{errorNameOfThePersonInCharge}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('company.phoneNumberOfThePersonInCharge')}</h6>
                                        <div className='phone'>
                                            <div className="form-control phone-country">
                                                <PhoneInput
                                                    defaultCountry="ES"
                                                    international
                                                    value={showModalNew ? modalCountryCodeNew : modalCountryCode}
                                                    onChange={(value) => {
                                                        setErrorPhoneNumberOfThePersonInCharge("");
                                                        if (!value) {
                                                            if (showModalNew) setModalCountryCodeNew("+");
                                                            else setModalCountryCode("+");
                                                            return;
                                                        }
                                                        const numericPart = value.replace(/\D/g, "");
                                                        const formatted = "+" + numericPart;
                                                        const finalValue = formatted.slice(0, 4);
                                                        if (showModalNew) setModalCountryCodeNew(finalValue);
                                                        else setModalCountryCode(finalValue);
                                                    }}
                                                    disabled={showModalDelete || (showModalEdit && !modalName)}
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                className={`form-control ${errorPhoneNumberOfThePersonInCharge ? 'is-invalid' : ''}`}
                                                placeholder={`${t('company.number')}`}
                                                value={showModalNew ? modalPhoneNumberNew : modalPhoneNumber}
                                                onChange={(e) => {
                                                    setErrorPhoneNumberOfThePersonInCharge('');
                                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                                    if (onlyNumbers.length <= 13) {
                                                        if (showModalNew) { setModalPhoneNumberNew(onlyNumbers); }
                                                        if (!showModalNew) { setModalPhoneNumber(onlyNumbers); }
                                                    } else {
                                                        toast.error(`${t('company.youCanOnlyEnterUpTo13Digits')}.`);
                                                    }
                                                }}
                                                disabled={showModalDelete || (showModalEdit && !modalName)}
                                            />
                                            <input
                                                type="text"
                                                className={`form-control ${errorPhoneNumberOfThePersonInCharge ? 'is-invalid' : ''}`}
                                                placeholder="Ext"
                                                value={showModalNew ? modalPhoneExtensionNew : modalPhoneExtension}
                                                onChange={(e) => {
                                                    setErrorPhoneNumberOfThePersonInCharge('');
                                                    const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                                    if (onlyNumbers.length <= 6) {
                                                        if (showModalNew) { setModalPhoneExtensionNew(onlyNumbers); }
                                                        if (!showModalNew) { setModalPhoneExtension(onlyNumbers); }
                                                    } else {
                                                        toast.error(`${t('company.youCanOnlyEnterUpTo6Digits')}.`);
                                                    }
                                                }}
                                                disabled={showModalDelete || (showModalEdit && !modalName)} />
                                        </div>
                                    </div>
                                    {errorPhoneNumberOfThePersonInCharge && (
                                        <div className="invalid-feedback d-block">{errorPhoneNumberOfThePersonInCharge}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6>{t('company.emailOfThePersonInCharge')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorEmailOfThePersonInCharge ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalEmailPersonChargeNew : modalEmailPersonCharge}
                                            onChange={(e) => {
                                                if (showModalNew) { setModalEmailPersonChargeNew(e.target.value); }
                                                if (!showModalNew) { setModalEmailPersonCharge(e.target.value); }
                                                setErrorEmailOfThePersonInCharge('');
                                            }}
                                            placeholder={`${t('company.enterThe1')} ${t('company.emailOfThePersonInCharge').toLowerCase()}`}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                        />
                                    </div>
                                    {errorEmailOfThePersonInCharge && (
                                        <div className="invalid-feedback d-block">{errorEmailOfThePersonInCharge}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('company.companysDefaultLanguage')}</h6>
                                        <select
                                            className={`form-control ${errorCompanysDefaultLanguage ? 'is-invalid' : ''}`}
                                            value={showModalNew ? modalCompanyLanguageNew : modalCompanyLanguage}
                                            onChange={(e) => {
                                                if (showModalNew) { setModalCompanyLanguageNew(e.target.value); }
                                                if (!showModalNew) { setModalCompanyLanguage(e.target.value); }
                                                setErrorCompanysDefaultLanguage('');
                                            }}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                        >
                                            <option value="">{t('company.selectAnOption')}</option>
                                            <option value="English">{t('company.english')}</option>
                                            <option value="Spanish">{t('company.spanish')}</option>
                                            <option value="Euskera">{t('menuLanguage.basque')}</option>
                                        </select>
                                    </div>
                                    {errorCompanysDefaultLanguage && (
                                        <div className="invalid-feedback d-block">{errorCompanysDefaultLanguage}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('company.inclusionDate')}</h6>
                                        <DatePicker
                                            selected={showModalNew ? modalInclusionDateNew ? new Date(modalInclusionDateNew) : null : modalInclusionDate ? new Date(modalInclusionDate) : null}
                                            onChange={(date: Date | null) => {
                                                setErrorInclusionDate('');
                                                if (date) {
                                                    if (showModalNew) { setModalInclusionDateNew(date.toISOString()); }
                                                    if (!showModalNew) { setModalInclusionDate(date.toISOString()); }
                                                } else {
                                                    if (showModalNew) { setModalInclusionDateNew(""); }
                                                    if (!showModalNew) { setModalInclusionDate(""); }
                                                }
                                            }}
                                            showTimeSelect
                                            timeFormat="HH:mm:ss"
                                            timeIntervals={1}
                                            dateFormat="dd/MM/yyyy, HH:mm:ss"
                                            timeCaption="Time"
                                            className={`form-control ${errorInclusionDate ? 'is-invalid' : ''}`}
                                            maxDate={new Date()}
                                            placeholderText={`${t('company.selectA1')} ${t('company.inclusionDate').toLowerCase()} (UTC)`}
                                            disabled={showModalDelete || (showModalEdit && !modalName)}
                                            portalId="root-portal"
                                            popperPlacement="bottom-start"
                                            popperClassName="custom-datepicker-popper"
                                        />
                                    </div>
                                    {errorInclusionDate && (
                                        <div className="invalid-feedback d-block">{errorInclusionDate}</div>
                                    )}
                                </div>
                                <div className='formBtn'>
                                    <button className={showModalDelete ? 'btn btn-danger' : 'btn btn-dark'} onClick={() => {
                                        if (showModalNew) handleAddCompany();
                                        if (showModalEdit) handleUpdateCompany();
                                        if (showModalDelete) handleDeleteCompany();
                                    }}
                                        disabled={loadingBtn}>
                                        {loadingBtn ? (
                                            <div className="loading-btn">
                                                <ClipLoader color="#007bff" size={20} />
                                            </div>
                                        ) : (
                                            showModalNew ? t('company.addCompany') : showModalEdit ? t('company.editCompany') : showModalDelete ? t('company.deleteCompany') : ''
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {(showModalBillingNew || showModalBillingEdit) && (
                        <div className='modalOverlay' onClick={handleOverlayClick}>
                            <div className='modalContentBilling'>
                                <div className='title'>
                                    <h6>
                                        {showModalBillingNew ? t('registers.newConnection') : showModalBillingEdit ? t('registers.editConnection') : ''}
                                    </h6>
                                    <span className="closeIcon" onClick={() => {
                                        closeModal();
                                    }}>&times;</span>
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('registers.vessel')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorVessel ? 'is-invalid' : ''}`}
                                                value={showModalBillingNew ? modalVesselNew : modalVessel}
                                                onChange={(e) => {
                                                    if (showModalBillingNew) { setModalVesselNew(e.target.value); }
                                                    if (!showModalBillingNew) { setModalVessel(e.target.value); }
                                                    setErrorVessel('');
                                                }}
                                                onFocus={handleSelectFocusVessel}
                                                onBlur={handleSelectBlurVessel}
                                                disabled={role === "costumer" && filteredDataVessel.length === 1}
                                            >
                                                {role !== "costumer" && (
                                                    <option value="">{`${t('registers.selectA1')} ${t('registers.vessel').toLowerCase()}`} ...</option>
                                                )}

                                                {filteredDataVessel.map((item) => (
                                                    <option key={item.Nembarcacion} value={String(item.Tembarcacion)}>
                                                        {item.Tembarcacion}
                                                    </option>
                                                ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenVessel ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorVessel && (
                                        <div className="invalid-feedback d-block">{errorVessel}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('registers.gridPoint')}</h6>
                                        <div className="select-wrapper">
                                            <select
                                                className={`form-control ${errorGridPoint ? 'is-invalid' : ''}`}
                                                value={showModalBillingNew ? modalGridPointNew : modalGridPoint}
                                                onChange={(e) => {
                                                    if (showModalBillingNew) { setModalGridPointNew(e.target.value); }
                                                    if (!showModalBillingNew) { setModalGridPoint(e.target.value); }
                                                    setErrorGridPoint('');
                                                }}
                                                onFocus={handleSelectFocusGridPoint}
                                                onBlur={handleSelectBlurGridPoint}
                                            >
                                                <option value="">{`${t('registers.selectA2')} ${t('registers.gridPoint').toLowerCase()}`} ...</option>
                                                {filteredDataGridPoint
                                                    .map((item) => (
                                                        <option key={item.Nmonaguillo} value={String(item.Tmonaguillo)}>
                                                            {item.Tmonaguillo}
                                                        </option>
                                                    ))}
                                            </select>
                                            <span className="icon">
                                                {isSelectOpenGridPoint ? (
                                                    <i className="fas fa-chevron-up"></i>
                                                ) : (
                                                    <i className="fas fa-chevron-down"></i>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    {errorGridPoint && (
                                        <div className="invalid-feedback d-block">{errorGridPoint}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('registers.startDate')} (UTC)</h6>
                                        <DatePicker
                                            selected={showModalBillingNew ? modalDateInicialNew ? new Date(modalDateInicialNew) : null : modalDateInicial ? new Date(modalDateInicial) : null}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    if (showModalBillingNew) { setModalDateInicialNew(date.toISOString()); setModalDateFinalNew(date.toISOString()); }
                                                    if (!showModalBillingNew) { setModalDateInicial(date.toISOString()); setModalDateFinal(date.toISOString()); }
                                                } else {
                                                    if (showModalBillingNew) { setModalDateInicialNew(""); setModalDateFinalNew(""); }
                                                    if (!showModalBillingNew) { setModalDateInicial(""); setModalDateFinal(""); }
                                                }
                                                setErrorStartDate('');
                                            }}
                                            showTimeSelect
                                            timeFormat="HH:mm:ss"
                                            timeIntervals={1}
                                            dateFormat="dd/MM/yyyy, HH:mm:ss"
                                            timeCaption="Time"
                                            className={`form-control ${errorStartDate ? 'is-invalid' : ''}`}
                                            maxDate={new Date()}
                                            placeholderText={`${t('registers.selectA1')} ${t('registers.startDate').toLowerCase()} (UTC)`}
                                            portalId="root-portal"
                                            popperPlacement="bottom-start"
                                            popperClassName="custom-datepicker-popper"
                                        />
                                    </div>
                                    {errorStartDate && (
                                        <div className="invalid-feedback d-block">{errorStartDate}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('registers.finalDate')} (UTC)</h6>
                                        <DatePicker
                                            selected={showModalBillingNew ? modalDateFinalNew ? new Date(modalDateFinalNew) : null : modalDateInicial ? new Date(modalDateInicial) : null}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    if (date.toISOString() >= modalDateInicialNew) {
                                                        if (showModalBillingNew) { setModalDateFinalNew(date.toISOString()); }
                                                        if (!showModalBillingNew) { setModalDateFinal(date.toISOString()); }
                                                    } else {
                                                        toast.error(`${t('registers.theEndDateCannotBeLessThanTheStartDate')}`);
                                                        setModalDateFinalNew(modalDateInicialNew);
                                                        if (showModalBillingNew) { setModalDateFinalNew(modalDateInicialNew); }
                                                        if (!showModalBillingNew) { setModalDateFinal(modalDateInicialNew); }

                                                    }
                                                } else {
                                                    setModalDateFinalNew("");
                                                }
                                                setErrorFinalDate('');
                                            }}
                                            showTimeSelect
                                            timeFormat="HH:mm:ss"
                                            timeIntervals={1}
                                            dateFormat="dd/MM/yyyy, HH:mm:ss"
                                            timeCaption="Time"
                                            className={`form-control ${errorFinalDate ? 'is-invalid' : ''}`}
                                            maxDate={new Date()}
                                            disabled={!modalDateInicialNew}
                                            placeholderText={`${t('registers.selectA1')} ${t('registers.finalDate').toLowerCase()} (UTC)`}
                                            portalId="root-portal"
                                            popperPlacement="bottom-start"
                                            popperClassName="custom-datepicker-popper"
                                        />
                                    </div>
                                    {errorFinalDate && (
                                        <div className="invalid-feedback d-block">{errorFinalDate}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('registers.initialConsumption')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorInitialConsumption ? 'is-invalid' : ''}`}
                                            value={showModalBillingNew ? modalInitialConsumptionNew : modalInitialConsumption}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === "" || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
                                                    if (showModalBillingNew) { setModalInitialConsumptionNew(value); }
                                                    if (!showModalBillingNew) { setModalInitialConsumption(value); }
                                                }
                                                setErrorInitialConsumption('');
                                            }}
                                            placeholder={`${t('registers.enterThe')} ${t('registers.initialConsumption').toLowerCase()}`}
                                        />
                                    </div>
                                    {errorInitialConsumption && (
                                        <div className="invalid-feedback d-block">{errorInitialConsumption}</div>
                                    )}
                                </div>
                                <div className='formInput'>
                                    <div className="input-row">
                                        <h6><span>*</span> {t('registers.finalConsumption')}</h6>
                                        <input
                                            type="text"
                                            className={`form-control ${errorFinalConsumption ? 'is-invalid' : ''}`}
                                            value={showModalBillingNew ? modalFinalConsumptionNew : modalFinalConsumption}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === "" || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
                                                    if (showModalBillingNew) { setModalFinalConsumptionNew(value); }
                                                    if (!showModalBillingNew) { setModalFinalConsumption(value); }
                                                }
                                                setErrorFinalConsumption('');
                                            }}
                                            disabled={!modalInitialConsumptionNew}
                                            placeholder={`${t('registers.enterThe')} ${t('registers.finalConsumption').toLowerCase()}`}
                                        />
                                    </div>
                                    {errorFinalConsumption && (
                                        <div className="invalid-feedback d-block">{errorFinalConsumption}</div>
                                    )}
                                </div>
                                <div className='formBtn' style={role === 'admin' ? { justifyContent: 'space-between' } : { justifyContent: 'end' }}>
                                    <div className='formBtn'>
                                        <button className='btn btn-dark' onClick={() => {
                                            if (showModalBillingNew) handleCreateConnection();
                                            if (showModalBillingEdit) handleUpdateConnection();
                                        }}
                                            disabled={loadingBtn}>
                                            {loadingBtn ? (
                                                <div className="loading-btn">
                                                    <ClipLoader color="#007bff" size={20} />
                                                </div>
                                            ) : (
                                                showModalBillingNew ? t('registers.createConnection') : showModalBillingEdit ? t('registers.updateConnection') : ''
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

};

export default CompanyComponent;